import type {
  AutofitMode,
  BodyProperties,
  Bullet,
  Hyperlink,
  LineSpacing,
  Paragraph,
  ParagraphProperties,
  PresentationAction,
  RunProperties,
  TextAlign,
  TextAnchor,
  TextBody,
  TextRun,
  UnderlineStyle,
} from '@slideforge/shared';
import { DEFAULT_BODY_PROPERTIES } from '@slideforge/shared';
import type { Element } from '../xml.js';
import { parseColorContainer, parseFill, parseLine, parsePercent, type FillContext } from './dml.js';

export interface TextContext extends FillContext {
  /** Resolves an `r:id` on a hyperlink to its target. */
  readonly hyperlinkFor: (relId: string) => Hyperlink | undefined;
}

export function parseTextBody(txBody: Element | undefined, ctx: TextContext): TextBody | undefined {
  if (!txBody) return undefined;
  return {
    bodyProps: parseBodyProperties(txBody.child('a:bodyPr')),
    paragraphs: txBody.all('a:p').map((p) => parseParagraph(p, ctx)),
  };
}

export function parseBodyProperties(bodyPr: Element | undefined): BodyProperties {
  if (!bodyPr) return DEFAULT_BODY_PROPERTIES;

  const anchor = bodyPr.attr('anchor');
  const vertical = bodyPr.attr('vert');
  const wrap = bodyPr.attr('wrap');

  return {
    anchor: (['t', 'ctr', 'b', 'just', 'dist'].includes(anchor ?? '')
      ? anchor
      : 't') as TextAnchor,
    anchorCenter: bodyPr.bool('anchorCtr', false),
    wrap: wrap === 'none' ? 'none' : 'square',
    insetLeft: bodyPr.int('lIns', DEFAULT_BODY_PROPERTIES.insetLeft),
    insetTop: bodyPr.int('tIns', DEFAULT_BODY_PROPERTIES.insetTop),
    insetRight: bodyPr.int('rIns', DEFAULT_BODY_PROPERTIES.insetRight),
    insetBottom: bodyPr.int('bIns', DEFAULT_BODY_PROPERTIES.insetBottom),
    autofit: parseAutofit(bodyPr),
    columnCount: bodyPr.int('numCol', 1),
    columnSpacing: bodyPr.int('spcCol', 0),
    vertical: (vertical ?? 'horz') as BodyProperties['vertical'],
    ...(bodyPr.attr('rot') ? { rotation: bodyPr.int('rot', 0) / 60000 } : {}),
  };
}

/**
 * PowerPoint bakes the *computed* shrink factor into `a:normAutofit`, so the
 * published slide can reproduce the on-screen size without re-running text
 * layout. Ignoring `fontScale` is why re-implementations overflow their boxes.
 */
function parseAutofit(bodyPr: Element): AutofitMode {
  if (bodyPr.has('a:spAutoFit')) return { kind: 'shape' };
  const norm = bodyPr.child('a:normAutofit');
  if (norm) {
    return {
      kind: 'normal',
      fontScale: parsePercent(norm.attr('fontScale') ?? '100000') / 100000,
      lineSpaceReduction: parsePercent(norm.attr('lnSpcReduction') ?? '0') / 100000,
    };
  }
  return { kind: 'none' };
}

export function parseParagraph(p: Element, ctx: TextContext): Paragraph {
  const pPr = p.child('a:pPr');
  const runs: TextRun[] = [];

  for (const child of p.children) {
    switch (child.name) {
      case 'a:r':
        runs.push({
          kind: 'text',
          text: child.child('a:t')?.innerText() ?? '',
          props: parseRunProperties(child.child('a:rPr'), ctx),
        });
        break;
      case 'a:br':
        runs.push({ kind: 'break', props: parseRunProperties(child.child('a:rPr'), ctx) });
        break;
      case 'a:fld':
        runs.push({
          kind: 'field',
          fieldType: child.attr('type') ?? 'unknown',
          text: child.child('a:t')?.innerText() ?? '',
          props: parseRunProperties(child.child('a:rPr'), ctx),
        });
        break;
      default:
        break;
    }
  }

  const endParaRPr = p.child('a:endParaRPr');
  return {
    props: parseParagraphProperties(pPr),
    runs,
    ...(endParaRPr ? { endProps: parseRunProperties(endParaRPr, ctx) } : {}),
  };
}

export function parseParagraphProperties(pPr: Element | undefined): ParagraphProperties {
  if (!pPr) return { level: 0 };
  const align = pPr.attr('algn');
  return {
    level: pPr.int('lvl', 0),
    ...(align ? { align: align as TextAlign } : {}),
    ...(pPr.attr('indent') ? { indent: pPr.int('indent', 0) } : {}),
    ...(pPr.attr('marL') ? { marginLeft: pPr.int('marL', 0) } : {}),
    ...(pPr.attr('marR') ? { marginRight: pPr.int('marR', 0) } : {}),
    ...(parseSpacing(pPr.child('a:lnSpc')) ? { lineSpacing: parseSpacing(pPr.child('a:lnSpc')) as LineSpacing } : {}),
    ...(parseSpacing(pPr.child('a:spcBef')) ? { spaceBefore: parseSpacing(pPr.child('a:spcBef')) as LineSpacing } : {}),
    ...(parseSpacing(pPr.child('a:spcAft')) ? { spaceAfter: parseSpacing(pPr.child('a:spcAft')) as LineSpacing } : {}),
    ...(parseBullet(pPr) ? { bullet: parseBullet(pPr) as Bullet } : {}),
    ...(pPr.attr('rtl') ? { rtl: pPr.bool('rtl', false) } : {}),
    ...(pPr.attr('defTabSz') ? { defaultTabSize: pPr.int('defTabSz', 0) } : {}),
  };
}

function parseSpacing(el: Element | undefined): LineSpacing | undefined {
  if (!el) return undefined;
  const pct = el.child('a:spcPct');
  if (pct) return { kind: 'percent', value: parsePercent(pct.attr('val') ?? '100000') / 1000 };
  const pts = el.child('a:spcPts');
  if (pts) return { kind: 'points', value: pts.int('val', 0) / 100 };
  return undefined;
}

function parseBullet(pPr: Element): Bullet | undefined {
  if (pPr.has('a:buNone')) return { kind: 'none' };

  const font = pPr.child('a:buFont')?.attr('typeface');
  const sizePct = pPr.child('a:buSzPct')?.attr('val');
  const color = parseColorContainer(pPr.child('a:buClr'));
  const common = {
    ...(font ? { font } : {}),
    ...(sizePct ? { sizePercent: parsePercent(sizePct) / 1000 } : {}),
    ...(color ? { color } : {}),
  };

  const buChar = pPr.child('a:buChar');
  if (buChar) return { kind: 'char', char: buChar.attr('char') ?? '•', ...common };

  const buAutoNum = pPr.child('a:buAutoNum');
  if (buAutoNum) {
    return {
      kind: 'autoNum',
      type: buAutoNum.attr('type') ?? 'arabicPeriod',
      startAt: buAutoNum.int('startAt', 1),
      ...common,
    };
  }

  const buBlip = pPr.child('a:buBlip');
  if (buBlip) {
    const embed = buBlip.path('a:blip')?.attr('r:embed');
    if (embed) return { kind: 'blip', mediaId: embed, ...common };
  }

  return undefined;
}

const UNDERLINE_VALUES = new Set<UnderlineStyle>([
  'none',
  'sng',
  'dbl',
  'heavy',
  'dotted',
  'dash',
  'dashLong',
  'dotDash',
  'wavy',
  'wavyDbl',
]);

export function parseRunProperties(rPr: Element | undefined, ctx: TextContext): RunProperties {
  if (!rPr) return {};

  const underline = rPr.attr('u');
  const strike = rPr.attr('strike');
  const caps = rPr.attr('cap');
  const fill = parseFill(rPr, ctx);
  const highlight = parseColorContainer(rPr.child('a:highlight'));
  const outline = parseLine(rPr.child('a:ln'), ctx);

  return {
    ...(rPr.attr('sz') ? { size: rPr.int('sz', 1800) } : {}),
    ...(rPr.attr('b') ? { bold: rPr.bool('b', false) } : {}),
    ...(rPr.attr('i') ? { italic: rPr.bool('i', false) } : {}),
    ...(underline && UNDERLINE_VALUES.has(underline as UnderlineStyle)
      ? { underline: underline as UnderlineStyle }
      : {}),
    ...(strike ? { strike: strike as RunProperties['strike'] } : {}),
    ...(caps ? { caps: caps as RunProperties['caps'] } : {}),
    ...(rPr.attr('spc') ? { spacing: rPr.int('spc', 0) } : {}),
    ...(rPr.attr('baseline') ? { baseline: rPr.int('baseline', 0) } : {}),
    ...(fill.kind !== 'inherit' ? { fill } : {}),
    ...(outline ? { outline } : {}),
    ...(highlight ? { highlight } : {}),
    ...(rPr.path('a:latin')?.attr('typeface') ? { latinFont: rPr.path('a:latin')?.attr('typeface') } : {}),
    ...(rPr.path('a:ea')?.attr('typeface') ? { eastAsianFont: rPr.path('a:ea')?.attr('typeface') } : {}),
    ...(rPr.path('a:cs')?.attr('typeface') ? { complexScriptFont: rPr.path('a:cs')?.attr('typeface') } : {}),
    ...(rPr.path('a:sym')?.attr('typeface') ? { symbolFont: rPr.path('a:sym')?.attr('typeface') } : {}),
    ...(rPr.attr('lang') ? { language: rPr.attr('lang') } : {}),
    ...(parseHyperlink(rPr, ctx) ? { hyperlink: parseHyperlink(rPr, ctx) as Hyperlink } : {}),
  };
}

const ACTION_VERBS: Readonly<Record<string, PresentationAction>> = {
  nextslide: 'nextslide',
  previousslide: 'previousslide',
  firstslide: 'firstslide',
  lastslide: 'lastslide',
  lastslideviewed: 'lastslideviewed',
  endshow: 'endshow',
};

export function parseHyperlink(
  container: Element | undefined,
  ctx: TextContext,
): Hyperlink | undefined {
  const click = container?.child('a:hlinkClick');
  if (!click) return undefined;

  const tooltip = click.attr('tooltip');
  const action = click.attr('action');
  if (action?.startsWith('ppaction://hlinkshowjump')) {
    const jump = /jump=([a-zA-Z]+)/.exec(action)?.[1]?.toLowerCase();
    const verb = jump ? ACTION_VERBS[jump] : undefined;
    if (verb) return { kind: 'action', verb, ...(tooltip ? { tooltip } : {}) };
  }

  const relId = click.attr('r:id');
  if (!relId) return undefined;
  const resolved = ctx.hyperlinkFor(relId);
  if (!resolved) return undefined;
  return tooltip ? { ...resolved, tooltip } : resolved;
}
